import Button from '@allesauto/core/button';
import LoaderOverlay from '@allesauto/core/loaderOverlay';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import translate from '@allesauto/i18n';
import { getPcpUrlForAccessories } from '@allesauto/utils/routeUtils';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  useAccessoriesBrands,
  useAccessoriesModels,
} from '@/features/Accessories/hooks';

const AccessoriesForm: FC = () => {
  const intl = useIntl();
  const whiteLabel = useWhiteLabel();

  const className = {
    label: 'flex flex-col mb-[6px]',
    dropdown: 'mb-20px w-full',
    button: 'mb-20px',
  };

  const router = useRouter();
  const { data, loading: brandsLoading } = useAccessoriesBrands();
  const {
    executeCall,
    data: modelsResponse,
    loading: modelsLoading,
  } = useAccessoriesModels();

  const [brand, setBrand] = useState<Option | null>(null);
  const [model, setModel] = useState<{
    value: string;
    label: string;
    variants: string[];
  } | null>(null);
  const [variant, setVariant] = useState<Option | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const handleSubmit = () => {
    const url = getPcpUrlForAccessories(
      brand?.label as string,
      model?.label,
      variant?.label as string
    );
    setSubmitLoading(true);
    if (window !== window.top) {
      // If we are in an iframe, break out of it by opening the target URL in the topmost frame.
      // This is typically the case when /src/pages/whitelabel/widget-iframe.tsx was embedded on a
      // merchant website and the search is triggered by the user.
      window.open(url.toString(), '_top');
    } else {
      router.push(url);
    }
  };

  const changeMarkDropDown = (v: Option | null) => {
    setBrand(v);
    setModel(null);
    setVariant(null);

    if (v) {
      executeCall(v.value as string);
    }
  };

  const changeModelDropDown = (v: Option | null) => {
    const selectedModel = modelsResponse?.models.find(
      (m) => m.model === v?.value ?? ''
    );

    setVariant(null);

    setModel(
      selectedModel
        ? {
            value: selectedModel.model,
            label: selectedModel.model,
            variants: selectedModel.variants ?? [],
          }
        : null
    );
  };

  const changeVariantDropDown = (v: Option | null) => {
    setVariant(v);
  };

  return (
    <>
      <div className={className.dropdown}>
        {(brandsLoading || modelsLoading) && (
          <LoaderOverlay hasLoader={false} hasZeroOpacity />
        )}
        <SearchDropdown
          isMulti={false}
          hasInfoIcon={false}
          options={data?.brands.map((brand) => ({
            value: brand.distributorId,
            label: brand.name,
          }))}
          placeholder={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.ACCESORIES_WIDGET.MARK_PLACEHOLDER'
          )}
          value={brand}
          onChange={(v) => changeMarkDropDown(v as Option | null)}
          context="accessories-widget-form-mark"
          darkBg={false}
          headline={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.ACCESORIES_WIDGET.TITLE'
          )}
          isLoading={brandsLoading}
        />
      </div>
      <div className={className.dropdown}>
        <SearchDropdown
          isMulti={false}
          disabled={!modelsResponse?.models.length}
          options={modelsResponse?.models.map((model) => ({
            value: model.model,
            label: model.model,
          }))}
          placeholder={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.ACCESORIES_WIDGET.MODEL_PLACEHOLDER'
          )}
          value={model}
          onChange={(v) => changeModelDropDown(v as Option | null)}
          context="accessories-widget-form-model"
          darkBg={false}
          isLoading={modelsLoading}
        />
      </div>
      <div className={className.dropdown}>
        <SearchDropdown
          isMulti={false}
          disabled={!model}
          options={model?.variants.map((variant) => ({
            value: variant,
            label: variant,
          }))}
          placeholder={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.ACCESORIES_WIDGET.VARIANT_PLACEHOLDER'
          )}
          value={variant}
          onChange={(v) => changeVariantDropDown(v as Option | null)}
          context="accessories-widget-form-variant"
          darkBg={false}
        />
      </div>
      <div className={className.button}>
        <Button
          onClick={handleSubmit}
          isLoading={submitLoading}
          fullWidth
          darkBg={false}
          whitelabel={!!whiteLabel}
          disabled={!brand}
        >
          {translate(intl, 'ui.QUICK_ENTRY_WIDGET.ACCESORIES_WIDGET.BUTTON')}
        </Button>
      </div>
    </>
  );
};

export default AccessoriesForm;
